import { useState, useEffect } from 'react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Slider } from '@/components/ui/slider';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ArrowUpDown, Filter, X, Check } from 'lucide-react';
import type { PracticeSeedListParams, SeedListStatus, SeedUsageMode } from '@/types/seeds';

export interface SeedFilterPanelProps {
  params: PracticeSeedListParams;
  categories: string[];
  onChange: (next: Partial<PracticeSeedListParams>) => void;
  onClear: () => void;
}

export function SeedFilterPanel({
  params,
  categories,
  onChange,
  onClear,
}: SeedFilterPanelProps) {
  // Local UI state for the range sliders — these only flush to the URL on
  // commit to avoid spamming the backend while dragging.
  const [difficultyRange, setDifficultyRange] = useState<[number, number]>([
    params.difficulty_min ?? 1,
    params.difficulty_max ?? 5,
  ]);
  const [minutesRange, setMinutesRange] = useState<[number, number]>([
    params.estimated_minutes_min ?? 1,
    params.estimated_minutes_max ?? 60,
  ]);
  const [completionRange, setCompletionRange] = useState<[number, number]>([
    params.completion_rate_min ?? 0,
    params.completion_rate_max ?? 100,
  ]);

  // Keep local slider state in sync if the URL changes externally (back/forward,
  // preset apply). This is a deliberate external-state-sync effect — the sliders
  // own their committed value locally during drag for UX reasons.
  useEffect(() => {
    /* eslint-disable react-hooks/set-state-in-effect */
    setDifficultyRange([params.difficulty_min ?? 1, params.difficulty_max ?? 5]);
    setMinutesRange([params.estimated_minutes_min ?? 1, params.estimated_minutes_max ?? 60]);
    setCompletionRange([params.completion_rate_min ?? 0, params.completion_rate_max ?? 100]);
    /* eslint-enable react-hooks/set-state-in-effect */
  }, [
    params.difficulty_min,
    params.difficulty_max,
    params.estimated_minutes_min,
    params.estimated_minutes_max,
    params.completion_rate_min,
    params.completion_rate_max,
  ]);

  const selectedCategories = params.categories ?? [];
  const status = (params.status ?? 'active') as SeedListStatus;
  const usageMode = (params.usage_mode ?? 'any') as SeedUsageMode;

  const toggleCategory = (cat: string) => {
    const next = selectedCategories.includes(cat)
      ? selectedCategories.filter((c) => c !== cat)
      : [...selectedCategories, cat];
    onChange({ categories: next });
  };

  // Count how many advanced filters are active so the "Filters" button shows a badge.
  const advancedCount = [
    (params.categories?.length ?? 0) > 0,
    params.difficulty_min != null,
    params.difficulty_max != null,
    params.estimated_minutes_min != null,
    params.estimated_minutes_max != null,
    params.completion_rate_min != null,
    params.completion_rate_max != null,
    params.usage_mode && params.usage_mode !== 'any',
    params.created_from,
    params.created_to,
    params.updated_from,
    params.updated_to,
  ].filter(Boolean).length;

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-3">
        <Tabs value={status} onValueChange={(v) => onChange({ status: v as SeedListStatus })}>
          <TabsList>
            <TabsTrigger value="active">Active</TabsTrigger>
            <TabsTrigger value="trash">Trash</TabsTrigger>
          </TabsList>
        </Tabs>

        <Popover>
          <PopoverTrigger
            render={(p) => (
              <Button {...p} variant="outline" size="sm">
                <Filter className="mr-2 h-4 w-4" />
                Filters
                {advancedCount > 0 && (
                  <Badge variant="secondary" className="ml-2 h-5 px-1.5">
                    {advancedCount}
                  </Badge>
                )}
              </Button>
            )}
          />
          <PopoverContent className="w-[420px] space-y-4 p-4" align="start">
            <div className="space-y-2">
              <Label>Categories</Label>
              <div className="flex max-h-32 flex-wrap gap-1 overflow-y-auto">
                {categories.length === 0 ? (
                  <span className="text-xs text-muted-foreground">No categories yet</span>
                ) : (
                  categories.map((c) => {
                    const active = selectedCategories.includes(c);
                    return (
                      <button
                        key={c}
                        type="button"
                        onClick={() => toggleCategory(c)}
                        className={`rounded-md border px-2 py-0.5 text-xs transition-colors ${
                          active
                            ? 'border-primary bg-primary text-primary-foreground'
                            : 'border-input bg-background hover:bg-accent'
                        }`}
                      >
                        {active && <Check className="mr-1 inline h-3 w-3" />}
                        {c}
                      </button>
                    );
                  })
                )}
              </div>
            </div>

            <div className="space-y-2">
              <Label>
                Difficulty: {difficultyRange[0]}–{difficultyRange[1]}
              </Label>
              <Slider
                min={1}
                max={5}
                step={1}
                value={difficultyRange}
                onValueChange={(v) => {
                  if (Array.isArray(v)) setDifficultyRange([v[0] ?? 1, v[1] ?? 5]);
                }}
                onValueCommitted={(v) => {
                  if (Array.isArray(v)) {
                    onChange({ difficulty_min: v[0], difficulty_max: v[1] });
                  }
                }}
              />
            </div>

            <div className="space-y-2">
              <Label>
                Estimated minutes: {minutesRange[0]}–{minutesRange[1]}
              </Label>
              <Slider
                min={1}
                max={60}
                step={1}
                value={minutesRange}
                onValueChange={(v) => {
                  if (Array.isArray(v)) setMinutesRange([v[0] ?? 1, v[1] ?? 60]);
                }}
                onValueCommitted={(v) => {
                  if (Array.isArray(v)) {
                    onChange({ estimated_minutes_min: v[0], estimated_minutes_max: v[1] });
                  }
                }}
              />
            </div>

            <div className="space-y-2">
              <Label>
                Completion rate: {completionRange[0]}%–{completionRange[1]}%
              </Label>
              <Slider
                min={0}
                max={100}
                step={5}
                value={completionRange}
                onValueChange={(v) => {
                  if (Array.isArray(v)) setCompletionRange([v[0] ?? 0, v[1] ?? 100]);
                }}
                onValueCommitted={(v) => {
                  if (Array.isArray(v)) {
                    onChange({ completion_rate_min: v[0], completion_rate_max: v[1] });
                  }
                }}
              />
            </div>

            <div className="space-y-2">
              <Label>Usage</Label>
              <div className="flex items-center gap-2">
                <Select
                  value={usageMode}
                  onValueChange={(v) =>
                    onChange({
                      usage_mode: (v ?? 'any') as SeedUsageMode,
                      usage_threshold: v === 'used_at_least' ? (params.usage_threshold ?? 1) : undefined,
                    })
                  }
                >
                  <SelectTrigger className="w-[180px]">
                    <SelectValue placeholder="Usage" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="any">Any</SelectItem>
                    <SelectItem value="unused">Unused</SelectItem>
                    <SelectItem value="used_at_least">Used at least N times</SelectItem>
                  </SelectContent>
                </Select>
                {usageMode === 'used_at_least' && (
                  <Input
                    type="number"
                    min={1}
                    value={params.usage_threshold ?? 1}
                    onChange={(e) =>
                      onChange({ usage_threshold: Number(e.target.value) || 1 })
                    }
                    className="w-20"
                  />
                )}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1">
                <Label className="text-xs">Created from</Label>
                <Input
                  type="date"
                  value={dateOnly(params.created_from)}
                  onChange={(e) => onChange({ created_from: toRFC3339(e.target.value) })}
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Created to</Label>
                <Input
                  type="date"
                  value={dateOnly(params.created_to)}
                  onChange={(e) => onChange({ created_to: toRFC3339(e.target.value, true) })}
                />
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button variant="ghost" size="sm" onClick={onClear}>
                Clear all
              </Button>
            </div>
          </PopoverContent>
        </Popover>

        <div className="ml-auto flex items-center gap-2">
          <Select
            value={params.sort_by ?? 'created_at'}
            onValueChange={(v) => onChange({ sort_by: v ?? 'created_at' })}
          >
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Sort by" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="created_at">Created</SelectItem>
              <SelectItem value="updated_at">Updated</SelectItem>
              <SelectItem value="seed_number">Number</SelectItem>
              <SelectItem value="title">Title</SelectItem>
              <SelectItem value="difficulty_level">Difficulty</SelectItem>
              <SelectItem value="problem_category">Category</SelectItem>
              <SelectItem value="completion_rate">Completion Rate</SelectItem>
            </SelectContent>
          </Select>

          <Button
            variant="outline"
            size="icon"
            onClick={() =>
              onChange({ sort_order: params.sort_order === 'desc' ? 'asc' : 'desc' })
            }
          >
            <ArrowUpDown className="h-4 w-4" />
            <span className="sr-only">
              {params.sort_order === 'desc' ? 'Descending' : 'Ascending'}
            </span>
          </Button>
        </div>
      </div>

      {/* Active-filter chips row. */}
      {selectedCategories.length > 0 && (
        <div className="flex flex-wrap items-center gap-1">
          <span className="text-xs text-muted-foreground">Categories:</span>
          {selectedCategories.map((c) => (
            <Badge key={c} variant="secondary" className="gap-1">
              {c}
              <button onClick={() => toggleCategory(c)} className="ml-1">
                <X className="h-3 w-3" />
              </button>
            </Badge>
          ))}
        </div>
      )}
    </div>
  );
}

function dateOnly(iso?: string): string {
  if (!iso) return '';
  return iso.slice(0, 10);
}

function toRFC3339(yyyymmdd: string, endOfDay = false): string | undefined {
  if (!yyyymmdd) return undefined;
  const time = endOfDay ? 'T23:59:59Z' : 'T00:00:00Z';
  return `${yyyymmdd}${time}`;
}
